<template>
  <div class="gallery-app">
    <!-- Hero Section with Album Selector -->
    <div class="hero-section">
      <div class="hero-content">

        
        <!-- Album Carousel -->
        <div class="albums-carousel">
          <button 
            class="nav-btn prev-btn" 
            @click="scrollCarousel('left')"
            :disabled="scrollPosition <= 0"
            aria-label="Scroll left"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M12 16l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>

          <div class="carousel-container" ref="carouselContainer" @scroll="handleScroll">
            <div class="carousel-track">
              <div
                v-for="(album, index) in albums"
                :key="index"
                class="album-card"
                :class="{ active: selectedAlbumIndex === index }"
                @click="selectAlbum(index)"
              >
                <div class="card-image">
                  <img :src="album.cover" :alt="album.title" loading="lazy" />
                  <div class="card-overlay">
                    <div class="overlay-content">
                      <h3>{{ album.title }}</h3>
                      <p>{{ album.photos.length }} photos</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <button 
            class="nav-btn next-btn" 
            @click="scrollCarousel('right')"
            :disabled="isScrollEnd"
            aria-label="Scroll right"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M8 16l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Gallery Popup -->
    <transition name="gallery-popup">
      <div v-if="showGalleryPopup" class="gallery-popup" @click.self="closeGalleryPopup">
        <div class="popup-container">
          <button class="popup-close" @click="closeGalleryPopup" aria-label="Close gallery">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>

          <div class="popup-header">
            <h2>{{ selectedAlbum.title }}</h2>
            <span class="photo-count">{{ selectedAlbum.photos.length }} Photos</span>
          </div>

          <div class="popup-photos-grid">
            <div
              v-for="(photo, index) in selectedAlbum.photos"
              :key="index"
              class="popup-photo-card"
              @click="openLightbox(index)"
            >
              <img :src="photo" :alt="`Photo ${index + 1}`" loading="lazy" />
              <div class="popup-photo-number">{{ index + 1 }}</div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Lightbox -->
    <transition name="lightbox">
      <div v-if="showLightbox" class="lightbox" @click.self="closeLightbox">
        <button class="lightbox-close" @click="closeLightbox" aria-label="Close lightbox">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>

        <button 
          v-if="currentPhotoIndex > 0"
          class="lightbox-nav prev"
          @click="previousPhoto"
          aria-label="Previous photo"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>

        <button 
          v-if="currentPhotoIndex < selectedAlbum.photos.length - 1"
          class="lightbox-nav next"
          @click="nextPhoto"
          aria-label="Next photo"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>

        <div class="lightbox-content">
          <transition :name="transitionName" mode="out-in">
            <img 
              :key="currentPhotoIndex"
              :src="selectedAlbum.photos[currentPhotoIndex]" 
              :alt="`Photo ${currentPhotoIndex + 1}`"
              class="lightbox-image"
            />
          </transition>
          
          <div class="lightbox-info">
            <span>{{ currentPhotoIndex + 1 }} / {{ selectedAlbum.photos.length }}</span>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>

import api from '@/services/api';
import { mediaUrl } from '@/services/media';

export default {
  name: 'GalleryApp',
  data() {
    return {
      selectedAlbumIndex: 0,
      showLightbox: false,
      showGalleryPopup: false,
      currentPhotoIndex: 0,
      transitionName: 'slide-right',
      scrollPosition: 0,
      isScrollEnd: false,
      loading: true,
      albums: [
        {
          title: 'UAE National Day 2022',
          cover: '/images/gallery/UAE National Day 2022/01.webp',
          photos: ['/images/gallery/UAE National Day 2022/01.webp']
        }
      ]
    }
  },
  computed: {
    selectedAlbum() {
      return this.albums[this.selectedAlbumIndex]
    }
  },
  async created() {
    try {
      const data = await api.getAlbums();
      const albumsList = api.extractList(data);
      if (albumsList.length > 0) {
        this.albums = albumsList.map(a => ({
          id: a.id,
          title: a.title,
          cover: mediaUrl(a.cover_photo || a.cover_image || ''),
          photos: (a.photos || []).map(p => mediaUrl(p.image || p))
        }));
      }
    } catch (e) {
      console.warn('[Gallery] API failed, using fallback:', e.message);
    }
    this.loading = false;
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeyPress)
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeyPress)
  },
  methods: {
    async selectAlbum(index) {
      this.selectedAlbumIndex = index;
      const album = this.albums[index];
      // Load photos from detail endpoint if not loaded yet
      if (album.id && album.photos.length === 0) {
        try {
          const detail = await api.getAlbum(album.id);
          const photos = detail.photos || [];
          album.photos = photos.map(p => mediaUrl(p.image || p));
          if (!album.cover && album.photos.length > 0) album.cover = album.photos[0];
        } catch (e) {
          console.log('Failed to load album photos', e.message);
        }
      }
      this.showGalleryPopup = true;
      document.body.style.overflow = 'hidden';
    },
    scrollCarousel(direction) {
      const container = this.$refs.carouselContainer;
      const scrollAmount = 320;
      if (direction === 'left') { container.scrollBy({ left: -scrollAmount, behavior: 'smooth' }); }
      else { container.scrollBy({ left: scrollAmount, behavior: 'smooth' }); }
    },
    handleScroll(event) {
      const container = event.target;
      this.scrollPosition = container.scrollLeft;
      this.isScrollEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - 10;
    },
    openLightbox(index) { this.currentPhotoIndex = index; this.showLightbox = true; document.body.style.overflow = 'hidden'; },
    closeLightbox() { this.showLightbox = false; document.body.style.overflow = 'auto'; },
    closeGalleryPopup() { this.showGalleryPopup = false; document.body.style.overflow = 'auto'; },
    nextPhoto() { if (this.currentPhotoIndex < this.selectedAlbum.photos.length - 1) { this.transitionName = 'slide-left'; this.currentPhotoIndex++; } },
    previousPhoto() { if (this.currentPhotoIndex > 0) { this.transitionName = 'slide-right'; this.currentPhotoIndex--; } },
    handleKeyPress(event) {
      if (event.key === 'Escape') {
        if (this.showLightbox) { this.closeLightbox(); }
        else if (this.showGalleryPopup) { this.closeGalleryPopup(); }
      } else if (this.showLightbox) {
        if (event.key === 'ArrowLeft') { this.previousPhoto(); }
        else if (event.key === 'ArrowRight') { this.nextPhoto(); }
      }
    }
  }
}

</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.gallery-app {
  min-height: 100vh;
  background: white;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
}

/* Hero Section */
.hero-section {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
}

.hero-content {
  width: 100%;
  max-width: 1400px;
  text-align: center;
}

.main-title {
  font-size: clamp(2.5rem, 6vw, 4.5rem);
  font-weight: 800;
  color: #2c3e50;
  margin-bottom: 1rem;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  letter-spacing: -0.02em;
}

.subtitle {
  font-size: clamp(1rem, 2vw, 1.25rem);
  color: #2c3e50;
  margin-bottom: 4rem;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}

/* Album Carousel */
.albums-carousel {
  position: relative;
  max-width: 100%;
}

.carousel-container {
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding: 2rem 0;
}

.carousel-container::-webkit-scrollbar {
  display: none;
}

.carousel-track {
  display: flex;
  gap: 1.5rem;
  padding: 0 2rem;
  min-width: min-content;
}

.album-card {
  flex: 0 0 auto;
  width: 280px;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.album-card:hover {
  transform: translateY(-8px);
}

.album-card.active {
  transform: scale(1.05) translateY(-8px);
}

.card-image {
  position: relative;
  width: 100%;
  height: 320px;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  background: rgba(255, 255, 255, 0.1);
}

.card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}

.album-card:hover .card-image img {
  transform: scale(1.1);
}

.card-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1.5rem;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.6) 60%, transparent 100%);
  color: white;
  transform: translateY(100%);
  transition: transform 0.3s ease;
}

.album-card:hover .card-overlay,
.album-card.active .card-overlay {
  transform: translateY(0);
}

.overlay-content h3 {
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
}

.overlay-content p {
  font-size: 0.875rem;
  opacity: 0.9;
}

/* Navigation Buttons */
.nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.95);
  color: #333;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  z-index: 10;
}

.nav-btn:hover:not(:disabled) {
  background: white;
  transform: translateY(-50%) scale(1.1);
  box-shadow: 0 6px 30px rgba(0, 0, 0, 0.3);
}

.nav-btn:disabled {
  opacity: 0;
  pointer-events: none;
}

.prev-btn {
  left: 0.5rem;
}

.next-btn {
  right: 0.5rem;
}

/* Gallery Popup */
.gallery-popup {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  backdrop-filter: blur(10px);
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  overflow-y: auto;
}

.popup-container {
  width: 100%;
  max-width: 1400px;
  background: white;
  border-radius: 24px;
  padding: 2rem;
  position: relative;
  max-height: 90vh;
  overflow-y: auto;
  animation: popupSlideUp 0.4s ease;
}

@keyframes popupSlideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.popup-close {
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: #f5f5f5;
  color: #333;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 10;
}

.popup-close:hover {
  background: #e0e0e0;
  transform: rotate(90deg);
}

.popup-header {
  margin-bottom: 2rem;
  padding-right: 3rem;
}

.popup-header h2 {
  font-size: clamp(1.75rem, 4vw, 2.5rem);
  font-weight: 800;
  color: #1a1a1a;
  margin-bottom: 0.5rem;
}

.popup-header .photo-count {
  font-size: 0.875rem;
  color: #666;
  background: #f5f5f5;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  display: inline-block;
}

.popup-photos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1.25rem;
}

.popup-photo-card {
  position: relative;
  aspect-ratio: 1;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  background: #f5f5f5;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.popup-photo-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.popup-photo-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}

.popup-photo-card:hover img {
  transform: scale(1.05);
}

.popup-photo-number {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.75rem;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.popup-photo-card:hover .popup-photo-number {
  opacity: 1;
}

/* Gallery Popup Transitions */
.gallery-popup-enter-active,
.gallery-popup-leave-active {
  transition: opacity 0.3s ease;
}

.gallery-popup-enter-from,
.gallery-popup-leave-to {
  opacity: 0;
}

.gallery-popup-enter-active .popup-container {
  animation: popupSlideUp 0.4s ease;
}

.gallery-popup-leave-active .popup-container {
  animation: popupSlideDown 0.3s ease;
}

@keyframes popupSlideDown {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(30px);
  }
}

/* Lightbox */
.lightbox {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.lightbox-close {
  position: fixed;
  top: 1rem;
  right: 1rem;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 1001;
}

.lightbox-close:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: rotate(90deg);
}

.lightbox-nav {
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 1001;
}

.lightbox-nav:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-50%) scale(1.1);
}

.lightbox-nav.prev {
  left: 1rem;
}

.lightbox-nav.next {
  right: 1rem;
}

.lightbox-content {
  max-width: 90vw;
  max-height: 90vh;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.lightbox-image {
  max-width: 100%;
  max-height: 85vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

.lightbox-info {
  margin-top: 1.5rem;
  padding: 0.75rem 1.5rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 30px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
}

/* Transitions */
.lightbox-enter-active,
.lightbox-leave-active {
  transition: opacity 0.3s ease;
}

.lightbox-enter-from,
.lightbox-leave-to {
  opacity: 0;
}

.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.3s ease;
}

.slide-left-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.slide-left-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

.slide-right-enter-from {
  opacity: 0;
  transform: translateX(-30px);
}

.slide-right-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

/* Responsive Design */
@media (max-width: 768px) {
  .hero-section {
    min-height: 80vh;
  }

  .main-title {
    font-size: 2.5rem;
  }

  .subtitle {
    margin-bottom: 3rem;
  }

  .album-card {
    width: 240px;
  }

  .card-image {
    height: 280px;
  }

  .popup-container {
    padding: 1.5rem;
    border-radius: 20px;
  }

  .popup-close {
    top: 1rem;
    right: 1rem;
  }

  .popup-header {
    margin-bottom: 1.5rem;
  }

  .popup-photos-grid {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 1rem;
  }

  .nav-btn {
    width: 40px;
    height: 40px;
  }

  .lightbox-nav {
    width: 40px;
    height: 40px;
  }

  .lightbox-close {
    width: 40px;
    height: 40px;
  }
}

@media (max-width: 480px) {
  .carousel-track {
    padding: 0 1rem;
    gap: 1rem;
  }

  .album-card {
    width: 200px;
  }

  .card-image {
    height: 240px;
    border-radius: 16px;
  }

  .popup-container {
    padding: 1rem;
    border-radius: 16px;
    max-height: 95vh;
  }

  .popup-photos-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .popup-header h2 {
    font-size: 1.5rem;
  }

  .popup-close {
    width: 36px;
    height: 36px;
  }
}

/* Tablet Landscape */
@media (min-width: 769px) and (max-width: 1024px) {
  .popup-photos-grid {
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  }
}

/* Large Screens */
@media (min-width: 1920px) {
  .popup-photos-grid {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  }

  .album-card {
    width: 320px;
  }

  .card-image {
    height: 360px;
  }
}

/* Accessibility */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    transition-duration: 0.01ms !important;
  }
}

button:focus-visible {
  outline: 3px solid #667eea;
  outline-offset: 3px;
}

/* Touch Device Optimization */
@media (hover: none) and (pointer: coarse) {
  .card-overlay {
    transform: translateY(0);
  }

  .popup-photo-number {
    opacity: 1;
  }
}
</style>